import React, { useState } from "react";
import { Card, Row, Col, Button } from "react-bootstrap";
import { ColumnDef } from "@tanstack/react-table";
import CurrencyFormat from "react-currency-format";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronUp,
  faChevronDown,
} from "@fortawesome/pro-solid-svg-icons";
import DataTable from "@components/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ScopeStackModal from "@components/ScopeStackModal/ScopeStackModal";
import FormNumberField from "@components/Forms/FormNumberField";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { PricingRow, PricingDetailRow } from "../types/PricingRow";
import useStyles from "../styles/useStyles";

interface Props {
  rows: PricingRow[];
  currencyUnit: string;
  isLoading: boolean;
  totalTcv: number;
  totalCost: number;
  totalProfit: number;
  totalMargin: number;
  paymentCreditsTotal: number | null;
  customerPaymentsTotal: number | null;
  showMarginModal: boolean;
  onOpenMarginModal: () => void;
  projectMargin: string;
  setProjectMargin: (value: string) => void;
  marginError: string[] | null;
  onApplyMargin: () => void;
  onCancelMargin: () => void;
}

interface Section {
  id: string;
  title: string;
  dataRows: PricingRow[];
}

interface DataTableRow {
  id: string;
  source: string;
  revenue: number;
  adjustment: number;
  net: number;
  tcv: number;
  cost: number;
  profit: number;
  margin: number | null;
  collapsedData?: any;
}

const groupIntoSections = (rows: PricingRow[]): Section[] => {
  const sections: Section[] = [];
  let current: Section | null = null;

  for (const row of rows) {
    if (row.rowType === "section") {
      current = { id: row.id, title: row.source, dataRows: [] };
      sections.push(current);
    } else if (row.rowType === "data" && current) {
      current.dataRows.push(row);
    }
  }

  return sections;
};

const Show: React.FC<Props> = ({
  rows,
  currencyUnit,
  isLoading,
  totalTcv,
  totalCost,
  totalProfit,
  totalMargin,
  paymentCreditsTotal,
  customerPaymentsTotal,
  showMarginModal,
  onOpenMarginModal,
  projectMargin,
  setProjectMargin,
  marginError,
  onApplyMargin,
  onCancelMargin,
}) => {
  const styles = useStyles();
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [id]: !(prev[id] ?? true),
    }));
  };

  const isSectionExpanded = (id: string) => expandedSections[id] ?? true;

  const formatCurrency = (value: number) => (
    <CurrencyFormat
      displayType="text"
      prefix={currencyUnit}
      isNumericString={true}
      thousandSeparator={true}
      value={value.toFixed(2)}
    />
  );

  const sections = groupIntoSections(rows);

  const detailColumns: ColumnDef<PricingDetailRow>[] = [
    {
      id: "source",
      header: "Source",
      cell: (props) => <span>{props.row.original.source}</span>,
    },
    {
      id: "revenue",
      header: "Revenue",
      cell: (props) => (
        <span>{formatCurrency(props.row.original.revenue)}</span>
      ),
    },
    {
      id: "adjustment",
      header: "Adjustment",
      cell: () => <span>---</span>,
    },
    {
      id: "net",
      header: "Net",
      cell: (props) => (
        <span>{formatCurrency(props.row.original.tcv)}</span>
      ),
    },
    {
      id: "tcv",
      header: "TCV",
      cell: (props) => (
        <span>{formatCurrency(props.row.original.tcv)}</span>
      ),
    },
    {
      id: "cost",
      header: "Cost",
      cell: (props) => (
        <span>{formatCurrency(props.row.original.cost)}</span>
      ),
    },
    {
      id: "profit",
      header: "Profit",
      cell: (props) => (
        <span>{formatCurrency(props.row.original.profit)}</span>
      ),
    },
    {
      id: "margin",
      header: "Margin",
      cell: (props) => {
        const margin = props.row.original.margin;
        if (margin === null || isNaN(margin)) return <span>---</span>;
        return <span>{margin.toFixed(2)}%</span>;
      },
    },
  ];

  const dataColumns: ColumnDef<DataTableRow>[] = [
    {
      id: "source",
      header: "Source",
      cell: (props) => <span>{props.row.original.source}</span>,
    },
    {
      id: "revenue",
      header: "Revenue",
      cell: (props) => (
        <span>{formatCurrency(props.row.original.revenue)}</span>
      ),
    },
    {
      id: "adjustment",
      header: "Adjustment",
      cell: (props) => (
        <span>{formatCurrency(props.row.original.adjustment)}</span>
      ),
    },
    {
      id: "net",
      header: "Net",
      cell: (props) => (
        <span>{formatCurrency(props.row.original.net)}</span>
      ),
    },
    {
      id: "tcv",
      header: "TCV",
      cell: (props) => (
        <span>{formatCurrency(props.row.original.tcv)}</span>
      ),
    },
    {
      id: "cost",
      header: "Cost",
      cell: (props) => (
        <span>{formatCurrency(props.row.original.cost)}</span>
      ),
    },
    {
      id: "profit",
      header: "Profit",
      cell: (props) => (
        <span>{formatCurrency(props.row.original.profit)}</span>
      ),
    },
    {
      id: "margin",
      header: "Margin",
      cell: (props) => {
        const margin = props.row.original.margin;
        if (margin === null || isNaN(margin)) return <span>---</span>;
        return <span>{margin.toFixed(2)}%</span>;
      },
    },
  ];

  const buildTableRows = (dataRows: PricingRow[]): DataTableRow[] =>
    dataRows.map((row) => {
      const hasDetails = (row.detailRows?.length ?? 0) > 0;
      return {
        id: row.id,
        source: row.source,
        revenue: row.revenue,
        adjustment: row.adjustment,
        net: row.net,
        tcv: row.tcv,
        cost: row.cost,
        profit: row.profit,
        margin: row.margin,
        ...(hasDetails
          ? {
              collapsedData: (
                <DataTable
                  data={row.detailRows!}
                  columns={detailColumns}
                  selectable={false}
                  classForTable={`${styles.detailTable} ${styles.hideDetailHead}`}
                  striped
                />
              ),
            }
          : {}),
      };
    });

  const renderSection = (section: Section) => {
    const sectionExpanded = isSectionExpanded(section.id);
    const hasExpandableRows = section.dataRows.some(
      (r) => (r.detailRows?.length ?? 0) > 0
    );
    const tableRows = buildTableRows(section.dataRows);

    return (
      <div key={section.id} className={styles.section}>
        <div
          className={styles.sectionHeader}
          onClick={() => toggleSection(section.id)}
        >
          <span className={styles.sectionTitle}>{section.title}</span>
          <FontAwesomeIcon
            icon={sectionExpanded ? faChevronUp : faChevronDown}
            className={styles.sectionChevron}
          />
        </div>
        {sectionExpanded && (
          <div className={styles.sectionBody}>
            <DataTable
              data={tableRows}
              columns={dataColumns}
              selectable={false}
              isCollapsible={hasExpandableRows}
              classForTable={styles.sectionTable}
              striped
            />
          </div>
        )}
      </div>
    );
  };

  const textRight = { textAlign: "right" as const };

  return (
    <div className={`${styles.page} ${styles.columnWidths}`}>
      {handleAlert(
        !!marginError,
        marginError || [],
        () => {},
        "warning",
        ToastAlert
      )}
      <Card className={styles.card}>
        <Card.Header className={styles.cardHeader}>
          <Row className={styles.headerRow}>
            <Col className={styles.title}>Overall Pricing</Col>
            <Col
              className="text-end"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Button className="btnSeawhite" onClick={onOpenMarginModal}>
                Adjust Project Margin
              </Button>
            </Col>
          </Row>
        </Card.Header>
        <hr className={styles.hr} />
        <Card.Body>
          {isLoading ? (
            <ScopeStackSpinner />
          ) : (
            <>
              {sections.map(renderSection)}
              {paymentCreditsTotal !== null && (
                <>
                  <Row className={styles.totalRow}>
                    <Col></Col>
                    <Col style={textRight} xs={4}>
                      Payment Credits
                    </Col>
                    <Col style={textRight}>
                      {formatCurrency(paymentCreditsTotal)}
                    </Col>
                    <Col></Col>
                    <Col></Col>
                    <Col></Col>
                  </Row>
                  <Row className={styles.totalRow}>
                    <Col></Col>
                    <Col style={textRight} xs={4}>
                      Customer Payments
                    </Col>
                    <Col style={textRight}>
                      {formatCurrency(customerPaymentsTotal!)}
                    </Col>
                    <Col></Col>
                    <Col></Col>
                    <Col></Col>
                  </Row>
                </>
              )}
            </>
          )}
        </Card.Body>
      </Card>
      <ScopeStackModal
        modalTitle="Edit Project Margin"
        modalBody={
          <div>
            <FormNumberField
              label="New Project Margin (%)"
              placeholder="Enter the percent you want the new project margin to be"
              step="0.01"
              value={projectMargin}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setProjectMargin(e.target.value)
              }
              min={undefined}
              max={undefined}
              required={false}
              defaultValue={undefined}
              readOnly={false}
              fieldFooter={undefined}
            />
          </div>
        }
        button1Text="Cancel"
        handleButton1Click={onCancelMargin}
        button2Text="Apply Margin"
        handleButton2Click={onApplyMargin}
        show={showMarginModal}
        handleClose={onCancelMargin}
      />
    </div>
  );
};

export default Show;
